import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Clock, User, CheckCircle, MessageSquare, Sparkles, Copy, CheckCheck } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { getAppToken } from '@/lib/oauthClient';

export default function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [infoRequestMessage, setInfoRequestMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // AI Analysis states
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);

  useEffect(() => {
    fetchTicket();
  }, [ticketId]);

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      const res = await axios.get(`${backendUrl}/api/support/tickets/${ticketId}`, {
        headers: { 'X-App-Token': appToken }
      });
      setTicket(res.data.ticket);
      setReplies(res.data.replies || []);
    } catch (error) {
      console.error('Erreur chargement ticket:', error);
      if (error.response?.status === 404) {
        toast.error('Ticket non trouvé');
        navigate('/admin/support/tickets');
      } else if (error.response?.status === 401) {
        toast.error('Session expirée, veuillez vous reconnecter');
        navigate('/login');
      } else {
        toast.error('Erreur lors du chargement du ticket');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!resolutionNotes.trim()) {
      toast.error('Veuillez ajouter des notes de résolution');
      return;
    }

    setActionLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      await axios.post(`${backendUrl}/api/support/tickets/${ticketId}/resolve`, {
        resolution_notes: resolutionNotes
      }, {
        headers: { 'X-App-Token': appToken }
      });
      
      toast.success('Ticket résolu avec succès');
      fetchTicket();
      setResolutionNotes('');
    } catch (error) {
      console.error('Erreur résolution ticket:', error);
      // Gérer les erreurs de validation Pydantic
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Erreur de validation Pydantic
          const errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Erreur de validation : ${errorMsg}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Erreur lors de la résolution');
        }
      } else {
        toast.error('Erreur lors de la résolution');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignToMe = async () => {
    setActionLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      await axios.post(`${backendUrl}/api/support/tickets/${ticketId}/assign`, {
        assigned_to: user.id
      }, {
        headers: { 'X-App-Token': appToken }
      });
      
      toast.success('Ticket assigné à vous avec succès');
      fetchTicket();
    } catch (error) {
      console.error('Erreur assignation ticket:', error);
      // Gérer les erreurs de validation Pydantic
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          const errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Erreur de validation : ${errorMsg}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Erreur lors de l\'assignation');
        }
      } else {
        toast.error('Erreur lors de l\'assignation');
      }
    } finally {
      setActionLoading(false);
    }
  };


  const handleRequestInfo = async () => {
    if (!infoRequestMessage.trim() || infoRequestMessage.trim().length < 10) {
      toast.error('Veuillez écrire un message d\'au moins 10 caractères');
      return;
    }

    setActionLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      await axios.post(`${backendUrl}/api/support/tickets/${ticketId}/request-info`, {
        message: infoRequestMessage
      }, {
        headers: { 'X-App-Token': appToken }
      });
      
      toast.success('Demande d\'informations envoyée à l\'utilisateur');
      setInfoRequestMessage('');
      fetchTicket();
    } catch (error) {
      console.error('Erreur demande info:', error);
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          const errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          toast.error(`Erreur : ${errorMsg}`);
        } else if (typeof detail === 'string') {
          toast.error(detail);
        } else {
          toast.error('Erreur lors de l\'assignation');
        }
      } else {
        toast.error('Erreur lors de l\'assignation');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleAiAnalysis = async () => {
    setAiLoading(true);
    setShowAiPanel(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const appToken = await getAppToken();
      const res = await axios.post(`${backendUrl}/api/support/ai/analyze/${ticketId}`, {}, {
        headers: { 'X-App-Token': appToken }
      });
      
      setAiAnalysis(res.data.analysis);
      toast.success('Analyse IA terminée');
    } catch (error) {
      console.error('Erreur analyse IA:', error);
      toast.error('Erreur lors de l\'analyse IA');
      setShowAiPanel(false);
    } finally {
      setAiLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };



  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Ticket introuvable</p>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const variants = {
      open: { variant: 'outline', text: 'Ouvert' },
      in_progress: { variant: 'default', text: 'En cours' },
      resolved: { variant: 'secondary', text: 'Résolu' },
      closed: { variant: 'secondary', text: 'Fermé' },
    };
    const config = variants[status] || variants.open;
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: { variant: 'outline', text: 'Basse' },
      medium: { variant: 'secondary', text: 'Moyenne' },
      high: { variant: 'default', text: 'Haute', className: 'bg-orange-500/10 text-orange-700' },
      urgent: { variant: 'destructive', text: 'Urgente' },
    };
    const config = variants[priority] || variants.medium;
    return <Badge variant={config.variant} className={config.className}>{config.text}</Badge>;
  };

  return (
    <div className="space-y-6 p-6" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/support/tickets')}
            data-testid="back-to-tickets-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="ticket-subject">{ticket.subject}</h1>
            <p className="text-sm text-muted-foreground" data-testid="ticket-id">
              Ticket #{ticket.id.substring(0, 8)}
            </p>
          </div>
        </div>
        
        {/* AI Analysis Button */}
        <Button
          onClick={handleAiAnalysis}
          disabled={aiLoading}
          variant="outline"
          className="gap-2"
          data-testid="ai-analyze-button"
        >
          <Sparkles className="h-4 w-4" />
          {aiLoading ? 'Analyse en cours...' : 'Analyser avec IA'}
        </Button>
      </div>

      {/* Ticket Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card data-testid="ticket-status-card">
          <CardHeader>
            <CardTitle className="text-sm">Statut</CardTitle>
          </CardHeader>
          <CardContent>
            {getStatusBadge(ticket.status)}
          </CardContent>
        </Card>

        <Card data-testid="ticket-priority-card">
          <CardHeader>
            <CardTitle className="text-sm">Priorité</CardTitle>
          </CardHeader>
          <CardContent>
            {getPriorityBadge(ticket.priority)}
          </CardContent>
        </Card>

        <Card data-testid="ticket-created-card">
          <CardHeader>
            <CardTitle className="text-sm">Créé le</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>{format(new Date(ticket.created_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}</span>
            </div>
          </CardContent>
        </Card>
      </div>


      {/* AI Analysis Panel */}
      {showAiPanel && aiAnalysis && (
        <Card className="border-purple-500/50 bg-purple-50/30 dark:bg-purple-950/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                Analyse IA
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAiPanel(false)}
              >
                Masquer
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Classification */}
            {aiAnalysis.classification && (
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">📊 Classification Suggérée</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Catégorie</p>
                    <Badge className="mt-1">{aiAnalysis.classification.category}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Priorité</p>
                    <Badge variant="destructive" className="mt-1">{aiAnalysis.classification.priority}</Badge>
                  </div>
                </div>
                <p className="text-sm mt-2 text-muted-foreground">
                  Confiance: {Math.round(aiAnalysis.classification.confidence * 100)}%
                </p>
                <p className="text-sm mt-1 italic">{aiAnalysis.classification.reasoning}</p>
              </div>
            )}

            {/* FAQ Response */}
            {aiAnalysis.faq_response && aiAnalysis.faq_response.suggested_response && (
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">💡 Réponse FAQ Suggérée</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(aiAnalysis.faq_response.suggested_response)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded text-sm whitespace-pre-wrap">
                  {aiAnalysis.faq_response.suggested_response}
                </div>
                {aiAnalysis.faq_response.is_faq && (
                  <Badge variant="secondary" className="mt-2 text-xs">
                    Question FAQ typique
                  </Badge>
                )}
              </div>
            )}

            {/* Support Actions */}
            {aiAnalysis.support_actions && (
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">🎯 Actions Recommandées</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  {aiAnalysis.support_actions.recommended_actions?.map((action, i) => (
                    <li key={i}>{action}</li>
                  ))}
                </ul>
                {aiAnalysis.support_actions.questions_to_ask?.length > 0 && (
                  <div className="mt-3">
                    <p className="font-semibold text-sm mb-1">Questions à poser:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      {aiAnalysis.support_actions.questions_to_ask.map((q, i) => (
                        <li key={`question-${i}-${q.substring(0, 20)}`}>{q}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Similar Tickets */}
            {aiAnalysis.similar_tickets && aiAnalysis.similar_tickets.length > 0 && (
              <div className="bg-white dark:bg-gray-900 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">🔍 Tickets Similaires Détectés ({aiAnalysis.similar_tickets.length})</h3>
                <div className="space-y-2">
                  {aiAnalysis.similar_tickets.map((similar) => (
                    <div key={similar.ticket_id || `similar-${similar.similarity_score}-${similar.reason?.substring(0, 10)}`} className="border-l-2 border-orange-500 pl-3 py-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">#{similar.ticket_id?.substring(0, 8)}</p>
                        <Badge variant="outline">{similar.similarity_score}% similaire</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{similar.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* Details */}
      <Card data-testid="ticket-details-card">
        <CardHeader>
          <CardTitle>Détails</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-semibold mb-1">Utilisateur</p>
            <div className="flex items-center gap-2" data-testid="ticket-user-email">
              <User className="h-4 w-4 text-muted-foreground" />
              <span>{ticket.user_email}</span>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold mb-1">Catégorie</p>
            <Badge variant="outline" data-testid="ticket-category">{ticket.category}</Badge>
          </div>

          <div>
            <p className="text-sm font-semibold mb-2">Description</p>
            <div className="bg-muted/50 p-4 rounded-lg" data-testid="ticket-description">
              <p className="whitespace-pre-wrap">{ticket.description}</p>
            </div>
          </div>

          {ticket.assigned_email && (
            <div data-testid="ticket-assigned-to">
              <p className="text-sm font-semibold mb-1">Assigné à</p>
              <span className="text-sm">{ticket.assigned_email}</span>
            </div>
          )}

          {ticket.resolution_notes && (
            <div data-testid="ticket-resolution">
              <p className="text-sm font-semibold mb-2">Notes de résolution</p>
              <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/20">
                <p className="whitespace-pre-wrap text-sm">{ticket.resolution_notes}</p>
                {ticket.resolved_at && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Résolu le {format(new Date(ticket.resolved_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation History */}
      {replies.length > 0 && (
        <Card data-testid="ticket-replies-card">
          <CardHeader>
            <CardTitle>Historique des échanges ({replies.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {replies.map((reply, index) => (
              <div key={reply.id}>
                {index > 0 && <Separator className="my-4" />}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {['admin', 'support'].includes(reply.user_role) 
                        ? `Support (${reply.user_email})` 
                        : `Utilisateur (${reply.user_email})`
                      }
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">
                      {format(new Date(reply.created_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                    </span>
                    {reply.is_internal && (
                      <Badge variant="secondary" className="text-xs">Note interne</Badge>
                    )}
                  </div>
                  <div className={`p-4 rounded-lg ${
                    ['admin', 'support'].includes(reply.user_role)
                      ? 'bg-blue-50 border-l-4 border-blue-500 dark:bg-blue-950/30'
                      : 'bg-orange-50 border-l-4 border-orange-500 dark:bg-orange-950/30'
                  }`}>
                    <p className="whitespace-pre-wrap text-sm">{reply.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
        <Card data-testid="ticket-actions-card">
          <CardHeader>
            <CardTitle>Actions Support</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!ticket.assigned_to && (
              <Button
                onClick={handleAssignToMe}
                disabled={actionLoading}
                variant="outline"
                className="w-full"
                data-testid="assign-ticket-button"
              >
                M'assigner ce ticket
              </Button>
            )}

            {/* Demander plus d'infos */}
            <div className="space-y-2">
              <Label htmlFor="info-request">Demander des informations supplémentaires</Label>
              <Textarea
                id="info-request"
                value={infoRequestMessage}
                onChange={(e) => setInfoRequestMessage(e.target.value)}
                placeholder="Décrivez les informations dont vous avez besoin..."
                rows={3}
                data-testid="info-request-textarea"
              />
              <Button
                onClick={handleRequestInfo}
                disabled={actionLoading}
                variant="outline"
                className="w-full"
                data-testid="request-info-button"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Demander des infos (48h pour répondre)
              </Button>
              <p className="text-xs text-muted-foreground">
                ⚠️ L'utilisateur recevra un email et aura 48h pour répondre, sinon le ticket se fermera automatiquement.
              </p>
            </div>

            {/* Résoudre le ticket */}
            <div className="space-y-2">
              <Label htmlFor="resolution">Résoudre le ticket</Label>
              <Textarea
                id="resolution"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Expliquez comment le problème a été résolu..."
                rows={4}
                data-testid="resolution-notes-textarea"
              />
              <Button
                onClick={handleResolve}
                disabled={actionLoading}
                className="w-full"
                data-testid="resolve-ticket-button"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Marquer comme résolu
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
